import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, FolderKanban, FileText, TrendingUp, Trash2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const moduleLabels = { ofertant: 'Licitatie Publica (SEAP)', privat: 'Licitatie Privata (RFI/RFP/RFQ)' };
const moduleColors = {
  ofertant: 'bg-blue-500/10 text-blue-400 ring-blue-500/20',
  privat: 'bg-amber-500/10 text-amber-400 ring-amber-500/20'
};
const procTypes = {
  ofertant: [
    { value: 'licitatie_deschisa', label: 'Licitatie Deschisa' },
    { value: 'licitatie_restransa', label: 'Licitatie Restransa' },
    { value: 'negociere', label: 'Negociere' },
    { value: 'dialog_competitiv', label: 'Dialog Competitiv' },
    { value: 'cerere_oferte', label: 'Cerere de Oferte' },
    { value: 'achizitie_directa', label: 'Achizitie Directa' },
  ],
  privat: [
    { value: 'rfp', label: 'RFP (Request for Proposal)' },
    { value: 'rfq', label: 'RFQ (Request for Quotation)' },
    { value: 'rfi', label: 'RFI (Request for Information)' },
  ]
};

const emptyForm = { name: '', description: '', module_type: 'ofertant', procurement_type: '', estimated_value: 0, currency: 'RON', deadline: '' };

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => { API.get('/projects').then(r => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Numele proiectului este obligatoriu'); return; }
    try {
      await API.post('/projects', form);
      toast.success('Proiect creat cu succes!');
      setForm(emptyForm);
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la creare'); }
  };

  const handleDelete = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Stergi acest proiect?')) return;
    try { await API.delete(`/projects/${id}`); toast.success('Proiect sters'); load(); } catch { toast.error('Eroare la stergere'); }
  };

  return (
    <div className="p-8" data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Proiecte</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestioneaza licitatiile si achizitiile tale</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-project-btn"><Plus className="w-4 h-4 mr-2" />Proiect Nou</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Proiect Nou</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Denumire proiect *</Label>
                <Input data-testid="project-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Ex: Licitatie modernizare retea" className="mt-1.5 bg-zinc-900/50 border-white/10" />
              </div>
              <div>
                <Label>Tip licitatie</Label>
                <Select value={form.module_type} onValueChange={v => setForm({...form, module_type: v, procurement_type: ''})}>
                  <SelectTrigger className="mt-1.5 bg-zinc-900/50 border-white/10" data-testid="project-module-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ofertant">Licitatie Publica (SEAP)</SelectItem>
                    <SelectItem value="privat">Licitatie Privata (RFI / RFP / RFQ)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.module_type === 'privat' ? 'Tip cerere' : 'Tip procedura'}</Label>
                <Select value={form.procurement_type} onValueChange={v => setForm({...form, procurement_type: v})}>
                  <SelectTrigger className="mt-1.5 bg-zinc-900/50 border-white/10" data-testid="project-proc-select">
                    <SelectValue placeholder={form.module_type === 'privat' ? 'Selecteaza RFI / RFP / RFQ' : 'Selecteaza procedura'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(procTypes[form.module_type] || []).map(pt => (
                      <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valoare estimata</Label>
                  <Input type="number" value={form.estimated_value} onChange={e => setForm({...form, estimated_value: parseFloat(e.target.value) || 0})}
                    className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" />
                </div>
                <div>
                  <Label>Termen limita</Label>
                  <Input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})}
                    className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" data-testid="project-deadline-input" />
                </div>
              </div>
              <div>
                <Label>Descriere</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="Descriere scurta a proiectului..." className="mt-1.5 bg-zinc-900/50 border-white/10 min-h-[80px]" />
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="submit-project-btn">
                <Plus className="w-4 h-4 mr-2" />Creeaza Proiect
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-white/5 animate-pulse rounded-lg" />)}
        </div>
      ) : projects.length === 0 ? (
        <Card className="bg-card border-white/10 border-dashed">
          <CardContent className="py-16 text-center">
            <FolderKanban className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground mb-2">Niciun proiect inca</p>
            <Button onClick={() => setOpen(true)} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />Creeaza primul proiect
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(proj => (
            <Link key={proj.id} to={`/projects/${proj.id}`} data-testid={`project-card-${proj.id}`}>
              <Card className="bg-card border-white/10 hover:border-white/20 transition-all duration-200 hover:-translate-y-0.5 h-full group">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <Badge variant="outline" className={`text-[10px] ${moduleColors[proj.module_type] || ''} ring-1 ring-inset border-0`}>
                      {moduleLabels[proj.module_type] || proj.module_type}
                    </Badge>
                    <button onClick={(e) => handleDelete(e, proj.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400"
                      data-testid={`delete-project-${proj.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <h3 className="font-semibold text-sm mb-2 line-clamp-2" style={{ fontFamily: 'Outfit' }}>{proj.name}</h3>
                  {proj.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-4">{proj.description}</p>}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-auto pt-3 border-t border-white/5">
                    <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{proj.documents_count} doc.</span>
                    {proj.win_score > 0 && (
                      <span className="flex items-center gap-1 text-emerald-400">
                        <TrendingUp className="w-3.5 h-3.5" />{proj.win_score}%
                      </span>
                    )}
                    {proj.deadline && <span className="ml-auto font-mono-data text-amber-400">{proj.deadline}</span>}
                  </div>
                  <div className="flex items-center gap-1 text-primary text-xs mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    Deschide <ArrowRight className="w-3 h-3" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
